// クラシック版の小さいボタンの定義（段階 C の2 の 5c-1。2026-10-25 に chibi.js と画面ファイル legacy.js・portrait.js から描き直した。動きは前と同じ）。
// メニューの一覧（名前・ランダム・TOP へ・閉じる・ヘルプ・全画面・復元）・完成・色を自由に選ぶ・色の入れかえ・左右の選択・空きのボタン。
// ボタンの定義は前と同じ形のオブジェクト（ID・STATUS・FILE・CLICK など）で、makeMenuButton・makeFloatButton と、ほかの所が chibi.btnXxx で使う。
// 系統の違い:
//   ボタンの絵: エンジンのまま（似顔絵も）はブラウザの言語で英語版、系統のある画面（前の legacy.js）は MENU_IMG_EN（storm）のときだけ英語版
//   名前のダイアログの文言 NAME_DIALOG: "new"（エンジンのまま）・"legacy"（系統のある画面＝genesis 系の文言と「保存時に公開」の注意）・"portrait"（genesis 系の文言と、入れないでの注意）
// mono の色の入れかえは画面ファイル gen1-pattern.js、第1世代の名前（縁に沿って表示のチェック付き。dressup 以外）は gen1.js が自前のもの（main.ts は上書きしない）
import { chibi, common } from './engine.js';
import { setAttr, setStyle } from './dom.js';
import { screen } from './colors.js';
import * as fx from './fx.js';
import type { Selection } from './selection.js';

type Engine = Record<string, any> & {
  PATH_IMG: string;
  SVG_FILE_KEY: string;
  ATTR_EDITLISTBUTTON: string;
  ATTR_EDITMENUBUTTON: string;
  ATTR_EDITFLOATBUTTON: string;
  ATTR_SAVEKEY: string;
  ATTR_AUTOSAVEKEY: string;
  ATTR_LAYER: string;
  ATTR_SAVEVER: string;
  DIV_F_CONTENTS: string;
  DIV_F_MAKE: string;
  SVG_TEXT: string;
  INP_F_PICKCOLOR: string;
  URI_TOP: string;
  COLOR_NAME: string;
  COLOR_NAME_WM: string;
  MODE_TEXT: boolean;
  MODE_TEXT_VALID_MIN: number;
  MODE_TEXT_VALID_MAX: number;
  isMobile: boolean;
  isViewText: boolean;
  svgMakeMenu: Selection;
  config<T>(key: string, def: T): T;
};
const E = (): Engine => chibi() as unknown as Engine;
type Common = Record<string, any>;
const C = (): Common => common() as unknown as Common;
const isJapanese = (): boolean => (window as unknown as { charat: { IsJapanese(): boolean } }).charat.IsJapanese();
const button = (): { next(btn: unknown, data: unknown): void; setStatus(sel: Selection, def: unknown): void } =>
  (window as unknown as { charat: { button: { next(btn: unknown, data: unknown): void; setStatus(sel: Selection, def: unknown): void } } }).charat.button;
const tt = (): Record<string, string> => (typeof _t !== 'undefined' && _t ? _t : {});

/** メニューの一覧の絵（ja: 日本語の絵、en: 英語の絵。英語版を使うかは系統で決まる） */
function menuFile(ja: string, en: string): string[] {
  const useEn = screen() === 'new' ? true : E().config('MENU_IMG_EN', false);
  return [E().PATH_IMG + (useEn && !isJapanese() ? en : ja)];
}

/** ボタンの定義を作る（読み込みの時点の言語・設定で絵を決める。前と同じ） */
export function makeMenuButtons(): Record<string, unknown> {
  const e = E();
  const P = e.PATH_IMG;
  const nameDialog = e.config('NAME_DIALOG', screen() === 'new' ? 'new' : 'legacy');
  const name = (): Element | null => document.querySelector('#' + E().SVG_TEXT + 'name');
  const btns: Record<string, any> = {};

  // 空き（サブメニューの段を埋める）
  btns.btnEditDummy = { ID: e.ATTR_EDITMENUBUTTON + 'Dummy', STATUS: 0, FILE: null, CLICK: null };
  // 復元作成（メニューには置いていない）
  btns.btnMenuRestore = {
    ID: e.ATTR_EDITLISTBUTTON + 'Restore',
    STATUS: 0,
    FILE: [P + 'switch/menu/bt_restore.svg'],
    CLICK: () => {
      E().btnEditMenu.OFF();
      E().loadChara(E().ATTR_SAVEKEY, true);
      E().btnMenuClose.CLICK();
    },
  };
  // ランダム（メニューを閉じてから、作成中の画面のあいだにランダムなキャラを作る）
  btns.btnMenuRandom = {
    ID: e.ATTR_EDITLISTBUTTON + 'Random',
    STATUS: 0,
    FILE: menuFile('switch/menu/bt_random.svg', 'switch/menu/bt_random_en.svg'),
    CLICK: () => {
      C().setNowMaking(true, 1, 0, 0, E().DIV_F_CONTENTS);
      E().btnEditMenu.OFF(50, () => {
        C().setNowMaking(false, 1, 1150, 300, E().DIV_F_CONTENTS);
        E().loadRandom();
        E().btnMenuClose.CLICK();
      });
    },
  };
  // 全画面（メニューには置いていない）
  btns.btnMenuFullscreen = {
    ID: e.ATTR_EDITLISTBUTTON + 'Fullscreen',
    STATUS: 0,
    FILE: isJapanese() ? [P + 'switch/menu/bt_fullscreen.svg'] : [P + 'switch/menu/bt_fullscreen_en.svg'],
    CLICK: () => {
      E().enterFullscreen();
      E().btnMenuClose.CLICK();
    },
  };
  // 復元コード（保存データの JSON）をクリップボードに（メニューに出るのはテスト環境だけ。https か localhost でしか使えず、失敗しても何も出さない）
  btns.btnMenuHelp = {
    ID: e.ATTR_EDITLISTBUTTON + 'Help',
    STATUS: 0,
    FILE: [P + 'switch/menu/bt_help.svg'],
    CLICK: () => {
      const en = E();
      fx.d3ClickCall('#' + en.btnEditMenu.ID + 'click', { BTN: en.btnEditMenu });
      const cstr = C().getSaveText(document.querySelectorAll(en.DIV_F_MAKE + " g[id^='" + en.ATTR_LAYER + "']"), en.btnMenuName.GET(true), en.ATTR_LAYER, en.ATTR_SAVEVER, true);
      navigator.clipboard.writeText(cstr);
    },
  };
  // TOP へ
  btns.btnMenuTop = {
    ID: e.ATTR_EDITLISTBUTTON + 'ToTop',
    STATUS: 0,
    FILE: menuFile('switch/menu/bt_toppage.svg', 'switch/menu/bt_toppage_en.svg'),
    CLICK: () => {
      location.replace(E().URI_TOP);
    },
  };
  // メニューを閉じる（メニューのボタンを押したのと同じ）
  btns.btnMenuClose = {
    ID: e.ATTR_EDITLISTBUTTON + 'Close',
    STATUS: 0,
    FILE: [P + 'switch/menu/bt_close.svg'],
    CLICK: () => {
      fx.d3ClickCall('#' + E().btnEditMenu.ID + 'click', { BTN: E().btnEditMenu });
    },
  };
  // 左右の選択（STATUS: 0=左右両方, 1=左, 2=右。画面に向かって見たときの左右）
  // 注意: setStatus に status を渡していないので、ボタンの絵は替わらない（STATUS だけ変わる。前と同じ）
  btns.btnSubSelect = {
    NAME: 'SELECT',
    STATUS: 0,
    FILE: [P + 'switch/select_lr.svg', P + 'switch/select_l.svg', P + 'switch/select_r.svg'],
    CLICK: (btn: unknown, btnData: unknown) => {
      button().next(btn, btnData);
    },
    SETSTATUS: (status: number) => {
      E().btnSubSelect.STATUS = status;
      button().setStatus(E().svgMakeMenu.select('#' + E().btnSubSelect.NAME), E().btnSubSelect);
    },
    RESET: () => {
      E().btnSubSelect.SETSTATUS(0);
    },
    ENABLED: () => {
      E().btnSubSelect.RESET();
    },
    EXEC: () => {},
  };
  // 完成（拡大を戻し、オートセーブと最後の保存の両方に書いてから完成画面）
  btns.btnSaveChara = {
    NAME: 'SAVE_CHARA',
    ID: e.ATTR_EDITFLOATBUTTON + 'SaveChara',
    STATUS: 0,
    FILE: [P + 'ico_right/finish.svg' + e.SVG_FILE_KEY],
    CLICK: () => {
      E().btnSaveChara.EXEC();
    },
    EXEC: () => {
      const en = E();
      if (en.btnSubZoom.IS_RESET() == true) en.btnSubZoom.RESET();
      en.saveChara(en.ATTR_AUTOSAVEKEY);
      en.saveChara(en.ATTR_SAVEKEY);
      en.makeComplete();
    },
  };
  // 色を自由に選ぶ（隠しの input を押して excolor の画面を開く）
  btns.btnPickColor = {
    NAME: 'PICK_COLOR',
    STATUS: 0,
    FILE: [P + 'switch/bt_color.svg' + e.SVG_FILE_KEY],
    CLICK: () => {
      fx.jqClick(E().INP_F_PICKCOLOR);
    },
  };
  // 色の入れかえ（柄の欄のピッカー。値を進めて塗るのは中身＝段階 B4、core/classic.ts の revColor）
  btns.btnRevColor = {
    NAME: 'REV_COLOR',
    STATUS: 0,
    FILE: [P + 'switch/bt_inverse.svg' + e.SVG_FILE_KEY],
    CLICK: () => {
      const en = E();
      if (en.enableEditPattern) {
        en.coreOps().revColor(en.selCategory, en.groupCategory);
        C().afterChangeColor();
      }
    },
  };
  // 名前（SVG の文字 #<SVG_TEXT>name。名前は属性 textdata、色は fill に持つ）
  btns.btnMenuName = {
    ID: e.ATTR_EDITLISTBUTTON + 'Name',
    STATUS: 0,
    FILE: menuFile('switch/menu/bt_nameinput.svg', 'switch/menu/bt_nameinput_en.svg'),
    // メニューを閉じて名前のダイアログを開く
    CLICK: () => {
      E().btnMenuClose.CLICK();
      E().btnMenuName.EXEC();
    },
    EXEC: () => {
      const cur = E().btnMenuName.GET();
      const t = tt();
      const label = nameDialog === 'new' ? t.name_label || 'Enter a name.' : t.name_label_genesis ? t.name_label_genesis : 'Give your avatar a name.';
      const caution =
        nameDialog === 'legacy'
          ? t.name_public_note
            ? t.name_public_note
            : '*The name will be public when saved.'
          : t.name_caution || "*Please don't enter inappropriate text.";
      const placeholder = nameDialog === 'new' ? t.name_placeholder || 'Enter a name' : t.name_placeholder_genesis ? t.name_placeholder_genesis : 'Enter a name';
      C().openDialog('input', [cur[0], label, placeholder, '', caution], E().btnMenuName.OK, E().btnMenuName.CLOSE);
    },
    // OK: 入力の名前を付けてオートセーブ（名前が空だったときは決まった色、付いていれば今の色）
    OK: () => {
      const en = E();
      const orgname = en.btnMenuName.GET();
      const el = name();
      const scol = orgname[0] == '' ? en.COLOR_NAME : el ? el.getAttribute('fill') : null;
      const nm = fx.inputValue('#chaNameDialog input').trim();
      en.btnMenuName.SET(nm, scol);
      en.saveChara(en.ATTR_AUTOSAVEKEY);
    },
    CLOSE: () => {},
    // 台詞のメーカー（MODE_TEXT）: 選んだパーツ番号が出す範囲なら文字を出す
    SETMODE: (anID: number) => {
      const en = E();
      const cur = en.btnMenuName.GET();
      en.isViewText = en.MODE_TEXT_VALID_MIN <= anID && anID <= en.MODE_TEXT_VALID_MAX;
      en.btnMenuName.SET(cur[0], cur[1]);
    },
    // 名前 aValue を色 aColor（省略は決まった色）で付ける
    SET: (aValue: string | null, aColor?: string | null) => {
      const en = E();
      const el = name();
      if (!el) return;
      const valid = aValue != '';
      setAttr(el, 'textdata', aValue);
      const ncolor = aColor != undefined ? aColor : en.COLOR_NAME;
      if (en.MODE_TEXT) {
        // 台詞: 入れていなければ「タップ／クリックして入力」を薄い色で
        const t = tt();
        setAttr(el, 'fill', valid ? ncolor : en.COLOR_NAME_WM);
        const text = valid ? aValue : en.isMobile ? (t.name_input_tap ? t.name_input_tap : '(Tap to enter)') : t.name_input_click ? t.name_input_click : '(Click to enter)';
        el.textContent = text == null ? '' : text;
        setStyle(el, 'display', en.isViewText ? 'block' : 'none');
      } else {
        // 名前: 入れていなければ隠す
        setAttr(el, 'fill', ncolor);
        el.textContent = aValue == null ? '' : aValue;
        setStyle(el, 'display', valid ? 'block' : 'none');
      }
    },
    // [名前, 色]。aQuat なら名前を encodeURIComponent（保存データ用）
    GET: (aQuat?: boolean) => {
      const el = name();
      let nm: string | null = el!.getAttribute('textdata');
      if (aQuat) nm = encodeURIComponent(nm as string);
      return [nm, el!.getAttribute('fill')];
    },
  };
  return btns;
}
